import { Injectable } from '@angular/core';
import { lastValueFrom, map, Observable, Subject } from 'rxjs';
import { TenantSource } from 'src/app/shared/models/tenant-source';
import { TenantSourceRequest } from '../../models/tenant-source-request';
import { TenantSourceResponseV3 } from '../../models/tenant-source-response-v3';
import { TenantApiService } from '../http/tenant-api.service';
import AutoMapperService from './auto-mapper.service';

@Injectable({
    providedIn: 'root'
})
export class TenantSourceService {
    private cachedSourcesTenantId: string | null = null;
    private cachedSources: TenantSource[] | null = null;
    private cachedSourcesRequest: Promise<TenantSource[]> | null = null;

    // broadcasts when tenant cache has cleared
    public get tenantSourceCacheCleared$(): Observable<any> {
        return this.tenantSourceCacheClearedSubject.asObservable();
    }
    private tenantSourceCacheClearedSubject = new Subject<void>();

    constructor(private tenantApiService: TenantApiService) {}

    getTenantSource(tenantId: string, sourceId: number) {
        return this.tenantApiService
            .getTenantSource(tenantId, sourceId)
            .pipe(
                map(response =>
                    AutoMapperService.mapToApplicationModelSameCase<TenantSourceResponseV3, TenantSource>(response)
                )
            );
    }

    getTenantSources(tenantId: string) {
        return this.tenantApiService.getTenantSources(tenantId).pipe(
            map(response => {
                return response?.map(data => {
                    const mappedData = AutoMapperService.mapToApplicationModelSameCase<
                        TenantSourceResponseV3,
                        TenantSource
                    >(data);
                    AutoMapperService.convertStringsToDates(mappedData, 'updatedTime');
                    return mappedData;
                });
            })
        );
    }

    public async getCachedTenantSources(tenantId: string): Promise<Array<TenantSource>> {
        if (this.cachedSources && this.cachedSourcesTenantId === tenantId) {
            this.clearCache();
        }

        try {
            if (this.cachedSources && this.cachedSourcesTenantId === tenantId) {
                return Promise.resolve(this.cachedSources);
            } else if (this.cachedSourcesRequest && this.cachedSourcesTenantId === tenantId) {
                return this.cachedSourcesRequest;
            } else {
                this.cachedSourcesRequest = lastValueFrom(this.getTenantSources(tenantId));
                this.cachedSourcesTenantId = tenantId;
                this.cachedSources = await this.cachedSourcesRequest;
                return Promise.resolve(this.cachedSources);
            }
        } catch (e) {
            this.clearCache();
            throw e;
        }
    }

    clearCache() {
        this.cachedSources = null;
        this.cachedSourcesRequest = null;
        this.tenantSourceCacheClearedSubject.next();
    }

    saveTenantSource(tenantSource: TenantSource, isEdit: boolean) {
        const request = AutoMapperService.mapToApplicationModelSameCase<TenantSource, TenantSourceRequest>(
            tenantSource
        );
        this.clearCache();

        if (isEdit) {
            return this.tenantApiService.updateTenantSource(request);
        }
        return this.tenantApiService.createTenantSource(request);
    }
}
